import {
  memo,
  useState,
  Suspense,
  lazy,
  useMemo,
  useRef,
  useCallback,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';
import { Layout, Menu, Typography, Space } from 'antd';
import type { CustomIconComponentProps } from '@ant-design/icons/lib/components/Icon';
import {
  AppstoreOutlined,
  AppstoreAddOutlined,
  BuildOutlined,
  InboxOutlined,
  GlobalOutlined,
  SettingOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Link, Route, Navigate, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'translations';
import { matchPath } from 'react-router';
import Version from 'components/helpers/Version';
import Copyright from 'components/helpers/Copyright';
import InvalidSession from 'features/validateSession';
import PageLayout from 'layouts/PageLayout';
import Loader from 'components/helpers/Loader';
import { LINKS } from 'configuration';

interface MenuItem {
  to: string;
  icon: ComponentType<Partial<CustomIconComponentProps>>;
  text: string;
  path: string[];
  component: LazyExoticComponent<ComponentType>;
  exact?: boolean;
  hiddenFromMenu?: boolean;
  isCollapsed?: boolean;
  disabled?: boolean;
  subMenu?: MenuItem[];
}

const { Sider } = Layout;
const { Text } = Typography;

const Support = lazy(() => import('../Support'));
const Pricing = lazy(() => import('../Pricing'));
const Dashboards = lazy(() => import('../Dashboards'));
const Devices = lazy(() => import('../Devices'));
const Wapps = lazy(() => import('../Wapps'));
const Account = lazy(() => import('../Account'));
const Settings = lazy(() => import('../Settings'));
const Wallet = lazy(() => import('../Wallet'));
const WhatsNew = lazy(() => import('../WhatsNew'));
const Store = lazy(() => import('../Store'));

const Main = memo(() => {
  const { tC, tCE } = useTranslation('pages', 'routers');
  const [openKeys, setOpenKeys] = useState([]);
  const [pageContent, setPageContent] = useState({});
  const location = useLocation();
  const menu = useMemo<MenuItem[]>(
    () => [
      {
        to: LINKS.dashboards,
        icon: BuildOutlined,
        text: tCE('menu.dashboards'),
        path: ['/', LINKS.dashboards],
        component: Dashboards,
        exact: true,
      },
      {
        to: LINKS.devices,
        icon: InboxOutlined,
        text: tCE('menu.devices'),
        path: [LINKS.devices],
        component: Devices,
      },
      {
        to: LINKS.wapps,
        icon: AppstoreOutlined,
        text: tCE('menu.installations'),
        path: [LINKS.wapps],
        component: Wapps,
      },
      {
        to: LINKS.pricing,
        icon: InboxOutlined,
        text: tCE('menu.pricing'),
        path: [LINKS.pricing],
        component: Pricing,
        hiddenFromMenu: true,
        isCollapsed: true,
      },
      {
        to: LINKS.account,
        icon: SettingOutlined,
        text: tCE('menu.account'),
        path: [LINKS.account, `${LINKS.account}/:tab`],
        exact: true,
        hiddenFromMenu: true,
        component: Account,
      },
      {
        to: LINKS.wallet,
        icon: WalletOutlined,
        text: tCE('menu.wallet'),
        path: [LINKS.wallet, `${LINKS.wallet}/:tab`],
        exact: true,
        hiddenFromMenu: true,
        component: Wallet,
      },
      {
        to: LINKS.settings,
        icon: SettingOutlined,
        text: tCE('menu.settings'),
        path: [LINKS.settings, `${LINKS.settings}/:tab`],
        exact: true,
        hiddenFromMenu: true,
        component: Settings,
      },
      {
        to: LINKS.store,
        icon: AppstoreAddOutlined,
        text: tCE('menu.store'),
        path: [`${LINKS.store}/*`],
        component: Store,
      },
      {
        to: LINKS.support,
        icon: GlobalOutlined,
        text: tCE('menu.support'),
        path: [LINKS.support],
        component: Support,
      },
      {
        to: LINKS.whatsnew,
        icon: GlobalOutlined,
        text: tCE('menu.whatsnew'),
        path: [LINKS.whatsnew],
        component: WhatsNew,
        hiddenFromMenu: true,
      },
    ],
    [tCE],
  );

  const menuItem = useMemo(() => {
    const currentRoute = location.pathname;
    let items;
    for (let i = 0; i < menu.length; i += 1) {
      const item = menu[i];

      const matched = item.path.filter((pattern) => matchPath(pattern, currentRoute));
      if (matched.length) {
        items = item;
        if (openKeys.length !== 0) {
          setOpenKeys([]);
        }
        break;
      }
      if (item.subMenu?.length) {
        items = item.subMenu.find((sub) =>
          sub.path.some((pattern) => matchPath(pattern, currentRoute)),
        );
        if (items) {
          if (openKeys[0] !== item.to) {
            setOpenKeys([item.to]);
          }
          break;
        }
      }
    }
    if (!items) {
      [items] = menu;
      if (openKeys.length !== 0) {
        setOpenKeys([]);
      }
    }
    return items;
  }, [openKeys, location, menu]);

  const [isCollapsed, setIsCollapsed] = useState(!!menuItem.isCollapsed);
  const skipCollapse = useRef(!!menuItem.isCollapsed);
  const onCollapse = useCallback((c) => {
    if (!skipCollapse.current) {
      setIsCollapsed(c);
    }
    skipCollapse.current = false;
  }, []);

  const toggleMenu = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  const createMenuItems = () => {
    const items = [];
    menu.forEach((item) => {
      if (!item.hiddenFromMenu) {
        items.push({
          key: item.to,
          label: <Link to={item.to}>{item.text}</Link>,
          icon: <item.icon />,
          disabled: item.disabled || false,
        });
      }
    });
    return items;
  };

  const createRouteItem = useCallback((item) => {
    if (!item.disabled && item.component) {
      let subRoutes = [];
      if (item.subMenu?.length) {
        subRoutes = item.subMenu.map(createRouteItem);
      }
      item.path.forEach((path) => {
        subRoutes.push(<Route path={path} element={<item.component />} />);
      });
      return subRoutes;
    }
    return null;
  }, []);

  return (
    <Layout hasSider>
      <Sider
        collapsible
        className="page-sider"
        collapsed={isCollapsed}
        onCollapse={onCollapse}
        breakpoint="md"
        trigger={null}
        collapsedWidth={0}>
        <div>
          <Link to="/">
            <div className="wappsto-logo" />
          </Link>
          <Menu
            theme="dark"
            mode="vertical"
            selectedKeys={[menuItem.to]}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            onSelect={setPageContent}
            items={createMenuItems()}
          />
        </div>
        <footer>
          <Space orientation="vertical">
            <Space size={5}>
              <Text className="font-sm">
                <Version />
              </Text>
              <Link to={LINKS.whatsnew} className="font-sm">
                {tC('menu.whatsnew')}
              </Link>
              <Text className="font-sm"> | </Text>
              <Link className="font-sm" to={LINKS.privacy} target="_blank">
                {tC('privacy')}
              </Link>
            </Space>
            <div>
              <Link className="font-sm" to={LINKS.seluxit} target="_blank">
                {tC('seluxit')}
              </Link>
              <Text className="font-sm">
                <Copyright />
              </Text>
            </div>
          </Space>
        </footer>
      </Sider>
      <PageLayout
        toggleMenu={toggleMenu}
        pageTitle={menuItem.text}
        pageContent={pageContent}
        isCollapsed={isCollapsed}
        onCollapse={onCollapse}>
        <Suspense fallback={<Loader />} key={location.pathname}>
          <Routes>
            <Route
              path={LINKS.subscriptions}
              element={<Navigate replace to={LINKS.activeSubscription} />}
            />
            {menu.map(createRouteItem)}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </PageLayout>

      <InvalidSession />
    </Layout>
  );
});

Main.displayName = 'Main';

export default Main;
